import { format } from "date-fns";
import { FieldValues } from "react-hook-form";
import { VariationSelection } from "../../../../../common/types/apiTypes";
import { formatSalaryComponents } from "../../../../../common/utils/formatSalaryComponents";
import { TesSalaryTableSelectionProvenance } from "../../../../tesSalaryTables/tesSalaryTablesApi";
import {
  OverlappableAssignment,
  OverlappableEmployment,
  SupplementPriceOverride,
} from "../../../../talents/talentAPI";
import {
  AcceptWorkAgreement,
  AddAssignment,
  OrderRowBundleId,
  WorkAgreement,
  WorkAgreementDocumentDownloadUrls,
  WorkAgreementInfo,
} from "../../../orderAPI";
import { SalaryType } from "../../../types/salaryType";
import { WorkPeriodType } from "../../../types/workPeriodType";
import { WorkTimeType } from "../../../types/workTimeType";
import { isoFormat } from "../../../../../common/helpers/formatDateTime";
import { TargetedShiftSalaryRow } from "../../../../employment/targetedShiftSalaries/targetedShiftSalariesApi";

export const WORK_AGREEMENT_DOCUMENT_FILE_TYPES = [
  ".pdf",
  ".jpg",
  ".jpeg",
  ".png",
];

export type DecodedWorkAgreementDocumentFile = {
  url: string;
  filename: string;
};

export type DecodedWorkAgreementDocumentDownloadUrls = {
  mainDocument?: DecodedWorkAgreementDocumentFile;
  attachments: DecodedWorkAgreementDocumentFile[];
};

const decodeFilename = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const getFilenameFromDownloadUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    const contentDisposition = parsed.searchParams.get(
      "response-content-disposition",
    );
    if (contentDisposition) {
      const quotedMatch = contentDisposition.match(/filename="([^"]+)"/i);
      if (quotedMatch?.[1]) {
        return decodeFilename(quotedMatch[1]);
      }
      const unquotedMatch = contentDisposition.match(/filename=([^;]+)/i);
      if (unquotedMatch?.[1]) {
        return decodeFilename(unquotedMatch[1].trim());
      }
    }
    const pathSegment = parsed.pathname.split("/").filter(Boolean).pop();
    return pathSegment ? decodeFilename(pathSegment) : "";
  } catch {
    return "";
  }
};

export const decodeWorkAgreementDocumentDownloadUrls = (
  downloadUrls: WorkAgreementDocumentDownloadUrls,
): DecodedWorkAgreementDocumentDownloadUrls => ({
  mainDocument: downloadUrls.mainDocument
    ? {
        url: downloadUrls.mainDocument.url,
        filename: getFilenameFromDownloadUrl(downloadUrls.mainDocument.url),
      }
    : undefined,
  attachments: (downloadUrls.attachments ?? []).map((attachment) => ({
    url: attachment.url,
    filename: getFilenameFromDownloadUrl(attachment.url),
  })),
});

export enum FormKeys {
  EezyCompanyId = "eezyCompanyId",
  EezyCompany = "eezyCompany",
  WorkTitleId = "workTitleId",
  WorkLocation = "workLocationName",
  Address = "address",
  TesId = "tesId",
  ReasonForTemporaryEmployment = "reasonForTemporaryEmployment",
  WorkStarts = "workStarts",
  WorkEnds = "workEnds",
  ProbationEndDate = "probationEndDate",
  WorkTimeType = "workTimeType",
  WorkPeriodRowType = "workPeriodRowType",
  MinHours = "minHours",
  MaxHours = "maxHours",
  WeeksInPeriod = "weeksInPeriod",
  PeriodType = "periodType",
  PeriodStartDateIsRequired = "periodStartDateIsRequired",
  PeriodStartDate = "periodStartDate",
  IsAveragingPeriodInUse = "isAveragingPeriodInUse",
  TalentAgreesExtraWork = "talentAgreesExtraWork",
  HolidayAccumulationRule = "holidayAccumulationRule",
  SchedulingClassId = "schedulingClassId",
  SalaryType = "salaryType",
  SalaryComponentDefaultValues = "salaryComponentDefaultValues",
  Components = "components",
  TotalSalary = "totalSalary",
  TesSalaryTableSelection = "tesSalaryTableSelection",
  TesVariation = "tesVariation",
  ExtraInformation = "extraInformation",
  MessageForExpert = "messageForExpert",
  InvoicingPriceOverride = "invoicingPriceOverride",
  Supplements = "supplements",
  SupplementPriceOverrides = "supplementPriceOverrides",
  ReplaceWorkAgreementId = "replaceWorkAgreementId",
  Status = "status",
  TargetedShiftSalarySpecs = "targetedShiftSalarySpecs",
  CreateHrTaskWhenTalentAcceptsAgreement = "createHrTaskWhenTalentAcceptsAgreement",
  WorkAgreementDocumentId = "workAgreementDocumentId",
}

export type WorkAgreementFormValues = {
  eezyCompanyId: string;
  status: string;
  workTitleId: string;
  workLocationName: string;
  address: string;
  tesId: string;
  totalSalary: string;
  tesVariation: VariationSelection[];
  reasonForTemporaryEmployment?: string;
  workStarts: Date | null;
  workEnds?: Date | null;
  probationEndDate: Date | null;
  workTimeType: WorkTimeType;
  minHours: string;
  maxHours: string;
  weeksInPeriod: string;
  periodType: string;
  periodStartDate: Date | null;
  isAveragingPeriodInUse: boolean;
  talentAgreesExtraWork: boolean;
  holidayAccumulationRule: string;
  schedulingClassId: string;
  salaryType: SalaryType;
  // TODO get salary default values some other way?
  salaryComponentDefaultValues?: { [key: string]: string };
  components: { [key: string]: string };
  tesSalaryTableSelection?: TesSalaryTableSelectionProvenance | null;
  supplements: any;
  extraInformation: string;
  messageForExpert: string;
  invoicingPriceOverride: string;
  supplementPriceOverrides: { [key: string]: SupplementPriceOverride };
  replaceWorkAgreementId: string | null;
  targetedShiftSalarySpecs: TargetedShiftSalaryRow[];
  createHrTaskWhenTalentAcceptsAgreement: boolean;
  workAgreementDocumentId: string | null;
};

export type FormSchedulingClassIds = {
  hourlySchedulingClassId: string;
  monthlySchedulingClassId: string;
};

export const dateFrom = (date: string | undefined | null): Date | null => {
  return date ? new Date(date) : null;
};

export const stringFrom = (numb: number | undefined | null): string => {
  return numb ? numb.toString() : "";
};

export const getSalaryDefaultValuesFromSalaryComponents = (
  salaryComponents: any,
) => {
  const salaryValues: { [key: string]: string } = {};
  for (const component of salaryComponents) {
    salaryValues[component.code] = "";
  }
  return salaryValues;
};

export const emptyFormValues: WorkAgreementFormValues = {
  [FormKeys.EezyCompanyId]: "",
  [FormKeys.WorkTitleId]: "",
  [FormKeys.WorkLocation]: "",
  [FormKeys.Status]: "",
  [FormKeys.Address]: "",
  [FormKeys.TesId]: "",
  [FormKeys.TesVariation]: [],
  [FormKeys.ReasonForTemporaryEmployment]: "",
  [FormKeys.WorkStarts]: null,
  [FormKeys.WorkEnds]: null,
  [FormKeys.ProbationEndDate]: null,
  [FormKeys.WorkTimeType]: WorkTimeType.FullTime,
  [FormKeys.MinHours]: "",
  [FormKeys.MaxHours]: "",
  [FormKeys.WeeksInPeriod]: "",
  [FormKeys.PeriodType]: "",
  [FormKeys.PeriodStartDate]: null,
  [FormKeys.IsAveragingPeriodInUse]: false,
  [FormKeys.TalentAgreesExtraWork]: true,
  [FormKeys.HolidayAccumulationRule]: "",
  [FormKeys.SchedulingClassId]: "",
  [FormKeys.SalaryType]: SalaryType.Hourly,
  //[FormKeys.SalaryComponentDefaultValues]: {},
  [FormKeys.TotalSalary]: "",
  [FormKeys.Components]: {},
  [FormKeys.TesSalaryTableSelection]: null,
  [FormKeys.Supplements]: {},
  [FormKeys.ExtraInformation]: "",
  [FormKeys.MessageForExpert]: "",
  [FormKeys.InvoicingPriceOverride]: "",
  [FormKeys.SupplementPriceOverrides]: {},
  [FormKeys.ReplaceWorkAgreementId]: null,
  [FormKeys.TargetedShiftSalarySpecs]: [],
  [FormKeys.CreateHrTaskWhenTalentAcceptsAgreement]: false,
  [FormKeys.WorkAgreementDocumentId]: null,
};

export const getWorkAgreementFormDefaultValues = (
  workAgreementInfo: WorkAgreementInfo,
  sourceEmployment?: OverlappableEmployment,
  sourceAssignment?: OverlappableAssignment,
): WorkAgreementFormValues => {
  const sourceEmp = sourceEmployment?.details;
  const salaryType = SalaryType.fromString(sourceEmp?.salaryType);
  const schedulingClassId =
    salaryType === SalaryType.Monthly
      ? workAgreementInfo.monthlySchedulingClassId
      : workAgreementInfo.schedulingClassId;
  return {
    [FormKeys.EezyCompanyId]: workAgreementInfo.eezyCompany.id,
    [FormKeys.Status]: "",
    [FormKeys.WorkTitleId]: workAgreementInfo.workTitleId,
    [FormKeys.WorkLocation]: workAgreementInfo.workLocation.name,
    [FormKeys.Address]: workAgreementInfo.workLocation.address.street,
    [FormKeys.TesId]: workAgreementInfo.tes.id,
    [FormKeys.TesVariation]: workAgreementInfo.tes.variations,
    [FormKeys.ReasonForTemporaryEmployment]:
      workAgreementInfo.reasonForTemporaryEmployment,
    [FormKeys.WorkStarts]: new Date(workAgreementInfo.workStarts),
    [FormKeys.WorkEnds]: workAgreementInfo.workEnds
      ? new Date(workAgreementInfo.workEnds)
      : null,
    [FormKeys.TotalSalary]: "",
    // Defaults to null if no source employment
    [FormKeys.ProbationEndDate]: dateFrom(sourceEmp?.probationEndDate),
    [FormKeys.WorkTimeType]: sourceEmp?.workTimeType ?? WorkTimeType.FullTime,
    [FormKeys.MinHours]: sourceEmp?.agreedHours?.minHours ?? "",
    [FormKeys.MaxHours]: sourceEmp?.agreedHours?.maxHours ?? "",
    [FormKeys.PeriodType]: sourceEmp?.periodType ?? "",
    [FormKeys.PeriodStartDate]:
      dateFrom(sourceEmp?.periodStartDate) ??
      dateFrom(workAgreementInfo?.periodStartDate),
    [FormKeys.IsAveragingPeriodInUse]:
      sourceEmp?.isAveragingPeriodInUse ?? false,
    [FormKeys.TalentAgreesExtraWork]: sourceEmp?.talentAgreesExtraWork ?? true,
    [FormKeys.HolidayAccumulationRule]:
      sourceEmp?.annualHolidayAccumulationRule ?? "",
    [FormKeys.SchedulingClassId]: schedulingClassId,
    [FormKeys.SalaryType]: salaryType || SalaryType.Hourly,
    [FormKeys.SalaryComponentDefaultValues]:
      sourceEmp?.salary?.ranges?.[0]?.components ??
      getSalaryDefaultValuesFromSalaryComponents(
        workAgreementInfo.tes.salaryComponents,
      ),
    [FormKeys.Components]: {},
    [FormKeys.TesSalaryTableSelection]: null,
    [FormKeys.Supplements]:
      workAgreementInfo.tes.supplements ??
      Object.fromEntries(
        Object.entries(sourceAssignment?.supplements ?? {}).map(
          ([key, value]) => [key, value.mode],
        ),
      ) ??
      {},
    [FormKeys.ExtraInformation]: "",
    [FormKeys.MessageForExpert]: "",
    [FormKeys.InvoicingPriceOverride]: "",
    [FormKeys.WeeksInPeriod]: stringFrom(sourceEmp?.agreedHours?.weeksInPeriod),
    [FormKeys.SupplementPriceOverrides]:
      workAgreementInfo!.tes.supplementPriceOverrides ??
      sourceAssignment?.supplementPriceOverrides ??
      {},
    [FormKeys.ReplaceWorkAgreementId]: null,
    [FormKeys.TargetedShiftSalarySpecs]: [],
    [FormKeys.CreateHrTaskWhenTalentAcceptsAgreement]: false,
    [FormKeys.WorkAgreementDocumentId]: null,
  };
};

export const getWorkAgreementFormValues = (
  payload: WorkAgreement,
): WorkAgreementFormValues => {
  const {
    workAgreement,
    status,
    workLocationName,
    workLocationAddress,
    workAgreementDocumentId,
  } = payload;

  return {
    [FormKeys.EezyCompanyId]: workAgreement.eezyCompanyId,
    [FormKeys.Status]: status,
    [FormKeys.WorkTitleId]: workAgreement.workTitleId,
    [FormKeys.WorkLocation]: workLocationName,
    [FormKeys.Address]: workLocationAddress.street,
    [FormKeys.TesId]: workAgreement.tesId,
    [FormKeys.TesVariation]: workAgreement.tesVariation,
    [FormKeys.ReasonForTemporaryEmployment]:
      workAgreement.tempEmploymentReasonCode,
    [FormKeys.WorkStarts]: new Date(workAgreement.startDate),
    ...(workAgreement.endDate && {
      [FormKeys.WorkEnds]: new Date(workAgreement.endDate),
    }),
    [FormKeys.TotalSalary]: "",
    [FormKeys.ProbationEndDate]: workAgreement.probationEndDate
      ? new Date(workAgreement.probationEndDate)
      : null,
    [FormKeys.WorkTimeType]: workAgreement.workTimeType,
    [FormKeys.MinHours]: workAgreement.agreedHours.minHours,
    [FormKeys.MaxHours]: workAgreement.agreedHours.maxHours,
    [FormKeys.WeeksInPeriod]:
      workAgreement.agreedHours.weeksInPeriod.toString(),
    [FormKeys.PeriodType]: "",
    [FormKeys.PeriodStartDate]: workAgreement?.periodStartDate
      ? new Date(workAgreement.periodStartDate)
      : null,
    [FormKeys.IsAveragingPeriodInUse]: false,
    [FormKeys.TalentAgreesExtraWork]: workAgreement.talentAgreesExtraWork,
    [FormKeys.HolidayAccumulationRule]:
      workAgreement.annualHolidayAccumulationRule,
    [FormKeys.SchedulingClassId]: workAgreement.paymentPeriodClassId,
    [FormKeys.SalaryType]: workAgreement.salaryType,
    [FormKeys.SalaryComponentDefaultValues]: workAgreement.salary,
    [FormKeys.Components]: workAgreement.salary,
    [FormKeys.TesSalaryTableSelection]:
      workAgreement.tesSalaryTableSelection || null,
    [FormKeys.Supplements]: workAgreement.supplements,
    [FormKeys.ExtraInformation]: workAgreement.extraInformation,
    [FormKeys.MessageForExpert]: workAgreement.messageForExpert,
    [FormKeys.InvoicingPriceOverride]: workAgreement.invoicingPriceOverride,
    [FormKeys.SupplementPriceOverrides]: workAgreement.supplementPriceOverrides,
    [FormKeys.ReplaceWorkAgreementId]: null,
    [FormKeys.TargetedShiftSalarySpecs]: (
      payload.targetedShiftSalarySpecs ?? []
    ).map((s) => ({
      keys: s.keys,
      salary: s.salary,
    })),
    [FormKeys.CreateHrTaskWhenTalentAcceptsAgreement]:
      payload.createHrTaskWhenTalentAcceptsAgreement ??
      workAgreement.createHrTaskWhenTalentAcceptsAgreement ??
      false,
    [FormKeys.WorkAgreementDocumentId]: workAgreementDocumentId,
  };
};

const handleDateForApi = (date: Date | null) => {
  if (date === null) return null;
  return format(date, isoFormat);
};

export const isWeeklyPeriodWithVariableWorkTime = (
  periodType: WorkPeriodType,
  workTimeType: WorkTimeType,
) =>
  periodType === WorkPeriodType.Weekly &&
  workTimeType === WorkTimeType.Variable;

export const isWeeklyPeriodWithFullOrPartTimeWorkTime = (
  periodType: WorkPeriodType,
  workTimeType: WorkTimeType,
) =>
  periodType === WorkPeriodType.Weekly &&
  (workTimeType === WorkTimeType.FullTime ||
    workTimeType === WorkTimeType.PartTime);

export const isOtherPeriodWithVariableWorkTime = (
  periodType: WorkPeriodType,
  workTimeType: WorkTimeType,
) =>
  periodType === WorkPeriodType.Other && workTimeType === WorkTimeType.Variable;

export const isOtherPeriodWithFullOrPartTimeWorkTime = (
  periodType: WorkPeriodType,
  workTimeType: WorkTimeType,
) =>
  periodType === WorkPeriodType.Other &&
  (workTimeType === WorkTimeType.FullTime ||
    workTimeType === WorkTimeType.PartTime);

interface EmploymentAgreedHours {
  minHours: string;
  maxHours: string;
  weeksInPeriod: number;
}

export const getEmploymentAgreedHours = (
  fieldValues: FieldValues,
): EmploymentAgreedHours => {
  const periodType = fieldValues[FormKeys.PeriodType] as WorkPeriodType;
  const workTimeType = fieldValues[FormKeys.WorkTimeType] as WorkTimeType;

  if (isWeeklyPeriodWithVariableWorkTime(periodType, workTimeType)) {
    return {
      minHours: fieldValues[FormKeys.MinHours],
      maxHours: fieldValues[FormKeys.MaxHours],
      weeksInPeriod: 1,
    };
  }

  if (isWeeklyPeriodWithFullOrPartTimeWorkTime(periodType, workTimeType)) {
    return {
      // minHours value to minHours and maxHours
      minHours: fieldValues[FormKeys.MinHours],
      maxHours: fieldValues[FormKeys.MinHours],
      weeksInPeriod: 1,
    };
  }

  if (isOtherPeriodWithVariableWorkTime(periodType, workTimeType)) {
    return {
      minHours: fieldValues[FormKeys.MinHours],
      maxHours: fieldValues[FormKeys.MaxHours],
      weeksInPeriod: Number(fieldValues[FormKeys.WeeksInPeriod]),
    };
  }

  if (isOtherPeriodWithFullOrPartTimeWorkTime(periodType, workTimeType)) {
    return {
      // minHours value to minHours and maxHours
      minHours: fieldValues[FormKeys.MinHours],
      maxHours: fieldValues[FormKeys.MinHours],
      weeksInPeriod: Number(fieldValues[FormKeys.WeeksInPeriod]),
    };
  }

  // This should never happen
  throw new Error(
    "getEmploymentAgreedHours: Invalid period type or work time type",
  );
};

const getSalaryValues = (salaryComponents: any) => {
  return formatSalaryComponents(salaryComponents);
};

export const createApiPayload = (
  bundleId: OrderRowBundleId,
  fieldValues: FieldValues,
) => {
  const supplementPriceOverrides =
    fieldValues[FormKeys.SupplementPriceOverrides];
  const supplementPriceOverrideValues: {
    [key: string]: SupplementPriceOverride;
  } = {};
  for (const key in supplementPriceOverrides) {
    const { value, unit } = supplementPriceOverrides[key];
    supplementPriceOverrideValues[key] = { value, unit };
  }

  const payload = {
    bundleId,
    startDate: handleDateForApi(fieldValues[FormKeys.WorkStarts]),
    ...(fieldValues[FormKeys.WorkEnds] && {
      endDate: handleDateForApi(fieldValues[FormKeys.WorkEnds]),
    }),
    ...(fieldValues[FormKeys.ProbationEndDate] && {
      probationEndDate: handleDateForApi(
        fieldValues[FormKeys.ProbationEndDate],
      ),
    }),
    workTimeType: fieldValues[FormKeys.WorkTimeType],
    agreedHours: getEmploymentAgreedHours(fieldValues),
    periodType: fieldValues[FormKeys.PeriodType],
    ...(fieldValues[FormKeys.PeriodStartDate] && {
      periodStartDate: handleDateForApi(fieldValues[FormKeys.PeriodStartDate]),
    }),
    talentAgreesExtraWork: fieldValues[FormKeys.TalentAgreesExtraWork],
    schedulingClass: fieldValues[FormKeys.SchedulingClassId],
    salaryType: fieldValues[FormKeys.SalaryType],
    salary: getSalaryValues(fieldValues[FormKeys.Components]),
    ...(fieldValues[FormKeys.TesSalaryTableSelection] && {
      tesSalaryTableSelection: fieldValues[FormKeys.TesSalaryTableSelection],
    }),
    supplements: fieldValues[FormKeys.Supplements],
    tesVariation: fieldValues[FormKeys.TesVariation],
    extraInformation: fieldValues[FormKeys.ExtraInformation],
    messageForExpert: fieldValues[FormKeys.MessageForExpert],
    isAveragingPeriodInUse: fieldValues[FormKeys.IsAveragingPeriodInUse],
    ...(fieldValues[FormKeys.InvoicingPriceOverride] && {
      invoicingPriceOverride: fieldValues[FormKeys.InvoicingPriceOverride],
    }),
    annualHolidayAccumulationRule:
      fieldValues[FormKeys.HolidayAccumulationRule],
    ...(supplementPriceOverrides && {
      supplementPriceOverrides: supplementPriceOverrideValues,
    }),
    ...(fieldValues[FormKeys.ReplaceWorkAgreementId] && {
      replaceWorkAgreementId: fieldValues[FormKeys.ReplaceWorkAgreementId],
    }),
    ...((
      fieldValues[FormKeys.TargetedShiftSalarySpecs] as TargetedShiftSalaryRow[]
    )?.length && {
      targetedShiftSalarySpecs: (
        fieldValues[
          FormKeys.TargetedShiftSalarySpecs
        ] as TargetedShiftSalaryRow[]
      ).map((row) => ({
        keys: {
          custCompanyId: row.keys.custCompany.id,
          custOrgs: [...row.keys.custOrgs.map((o) => o.id)].sort(),
          workLocs: [...row.keys.workLocs.map((wl) => wl.id)].sort(),
          workTitles: [...row.keys.workTitles].sort(),
        },
        salary: row.salary,
      })),
    }),
    createHrTaskWhenTalentAcceptsAgreement:
      fieldValues[FormKeys.CreateHrTaskWhenTalentAcceptsAgreement] ?? false,
  };

  return payload;
};

export const createAcceptWorkAgreementApiPayload = (
  talentId: string,
  bundleId: OrderRowBundleId,
  fieldValues: FieldValues,
): AcceptWorkAgreement => {
  return {
    workAgreementDocumentId: fieldValues[FormKeys.WorkAgreementDocumentId],
    workAgreement: {
      ...createApiPayload(bundleId, fieldValues),
      talentId,
    },
  };
};

/**
 * Helper to construct some parts of payload for add assignment.
 * TODO should we need this at all, mainly does supplements (and dates) only
 * @param bundleId
 * @param fieldValues
 */
export const createAddAssignmentApiPayload = (
  bundleId: OrderRowBundleId,
  fieldValues: FieldValues,
): AddAssignment => {
  const supplementPriceOverrides =
    fieldValues[FormKeys.SupplementPriceOverrides];
  const supplementPriceOverrideValues: {
    [key: string]: SupplementPriceOverride;
  } = {};
  for (const key in supplementPriceOverrides) {
    const { value, unit } = supplementPriceOverrides[key];
    supplementPriceOverrideValues[key] = { value, unit };
  }

  const payload = {
    bundleId,
    startDate: handleDateForApi(fieldValues[FormKeys.WorkStarts]),
    ...(fieldValues[FormKeys.WorkEnds] && {
      endDate: handleDateForApi(fieldValues[FormKeys.WorkEnds]),
    }),
    supplements: fieldValues[FormKeys.Supplements],
    ...(fieldValues[FormKeys.InvoicingPriceOverride] && {
      invoicingPriceOverride: fieldValues[FormKeys.InvoicingPriceOverride],
    }),

    ...(supplementPriceOverrides && {
      supplementPriceOverrides: supplementPriceOverrideValues,
    }),
    ...(fieldValues[FormKeys.InvoicingPriceOverride] && {
      invoicingPriceOverride: fieldValues[FormKeys.InvoicingPriceOverride],
    }),
    workTitleId: fieldValues.workTitleId,
  };

  return payload;
};

/**
 * Helper type to distinguish between work offer creation modes
 */
export enum WorkOfferType {
  /**
   * Creating a new work offer. This is the default mode.
   */
  New = "NEW",
  /**
   *  **Not yet implemented.** Not really creating a work offer, just cuts old assignment and makes new one.
   */
  ReplaceAndMerge = "REPLACE_AND_MERGE",
  /**
   * not really creating a work offer, just overlapping assignment
   */
  Overlapping = "OVERLAPPING",
}
